import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  MessageCircle, TrendingUp, TrendingDown, Zap, RefreshCw,
  Target, AlertTriangle, Sparkles, ArrowUpRight, ArrowDownRight, Star, Newspaper, HelpCircle
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";

interface SocialSignal {
  id: string;
  token_symbol: string;
  token_name: string | null;
  narrative: string | null;
  twitter_mentions_24h: number | null;
  twitter_mentions_7d_avg: number | null;
  twitter_sentiment_score: number | null;
  overall_social_score: number | null;
  galaxy_score: number | null;
  alt_rank: number | null;
  alt_rank_previous: number | null;
  mention_change_pct: number | null;
  is_spiking: boolean | null;
  spike_magnitude: string | null;
  current_price: number | null;
  price_change_24h: number | null;
  price_vs_social_divergence: number | null;
  signal_type: string | null;
  ai_sentiment_summary: string | null;
  trending_keywords: string[] | null;
  catalyst_detected: string | null;
  news_count_24h: number | null;
  baseline_days: number | null;
  data_source: string | null;
  collected_at: string;
}

const NARRATIVE_LABELS: Record<string, { label: string; emoji: string; color: string }> = {
  ai_agents: { label: 'AI', emoji: '🤖', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  depin: { label: 'DePIN', emoji: '📡', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  rwa: { label: 'RWA', emoji: '🏢', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  meme: { label: 'Meme', emoji: '🐸', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  defi: { label: 'DeFi', emoji: '💰', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  gaming: { label: 'Gaming', emoji: '🎮', color: 'bg-pink-500/20 text-pink-400 border-pink-500/30' },
  layer2: { label: 'L2', emoji: '⚡', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  layer1: { label: 'L1', emoji: '🔷', color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' },
  other: { label: 'Other', emoji: '📊', color: 'bg-secondary' },
};

type Tab = 'opportunities' | 'spiking' | 'altrank' | 'all';

export function SocialSignalAggregator() {
  const [signals, setSignals] = useState<SocialSignal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('opportunities');
  const [activeNarrative, setActiveNarrative] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => { fetchSignals(); }, []);

  const fetchSignals = async () => {
    try {
      const { data, error } = await supabase
        .from('social_signals')
        .select('*')
        .gte('collected_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('collected_at', { ascending: false });
      if (error) throw error;

      const unique = new Map<string, SocialSignal>();
      (data || []).forEach((s: any) => {
        if (!unique.has(s.token_symbol)) unique.set(s.token_symbol, s as SocialSignal);
      });
      const arr = Array.from(unique.values());
      setSignals(arr);
      if (arr.length > 0) setLastUpdated(new Date(arr[0].collected_at));
    } catch (err) {
      console.error('Error fetching signals:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refresh = async () => {
    setIsRefreshing(true);
    try {
      await supabase.functions.invoke('fetch-lunarcrush-social');
      await fetchSignals();
    } catch (err) {
      console.error(err);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Derived stats
  const stats = useMemo(() => {
    const spiking = signals.filter(s => s.is_spiking).length;
    const bull = signals.filter(s => s.signal_type === 'bullish_divergence').length;
    const early = signals.filter(s => s.signal_type === 'early_warning').length;
    return { total: signals.length, spiking, bull, early };
  }, [signals]);

  // Narrative grid: per-narrative aggregates
  const narrativeStats = useMemo(() => {
    const acc: Record<string, { count: number; spiking: number; avgGalaxy: number; avgChange: number }> = {};
    signals.forEach(s => {
      if (!s.narrative) return;
      const k = s.narrative;
      if (!acc[k]) acc[k] = { count: 0, spiking: 0, avgGalaxy: 0, avgChange: 0 };
      acc[k].count++;
      if (s.is_spiking) acc[k].spiking++;
      acc[k].avgGalaxy += (s.galaxy_score || 0);
      acc[k].avgChange += (s.mention_change_pct || 0);
    });
    Object.values(acc).forEach(v => {
      v.avgGalaxy = v.count ? v.avgGalaxy / v.count : 0;
      v.avgChange = v.count ? v.avgChange / v.count : 0;
    });
    return acc;
  }, [signals]);

  // Tab filtering
  const filtered = useMemo(() => {
    let list = signals;
    if (activeNarrative) list = list.filter(s => s.narrative === activeNarrative);
    if (activeTab === 'opportunities') {
      list = list.filter(s => s.signal_type === 'bullish_divergence' && (s.price_vs_social_divergence || 0) > 30);
      list.sort((a, b) => (b.price_vs_social_divergence || 0) - (a.price_vs_social_divergence || 0));
    } else if (activeTab === 'spiking') {
      list = list.filter(s => s.is_spiking);
      list.sort((a, b) => (b.mention_change_pct || 0) - (a.mention_change_pct || 0));
    } else if (activeTab === 'altrank') {
      list = list.filter(s => s.alt_rank && s.alt_rank_previous && (s.alt_rank_previous - s.alt_rank) > 20);
      list.sort((a, b) => ((b.alt_rank_previous || 0) - (b.alt_rank || 0)) - ((a.alt_rank_previous || 0) - (a.alt_rank || 0)));
    } else {
      list = [...list].sort((a, b) => (b.galaxy_score || 0) - (a.galaxy_score || 0));
    }
    return list;
  }, [signals, activeTab, activeNarrative]);

  if (isLoading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            Social Signal Aggregator
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const emptyDay = stats.total === 0;

  return (
    <TooltipProvider delayDuration={200}>
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <MessageCircle className="w-5 h-5 text-primary" />
              Social Signal Aggregator
              <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">
                LunarCrush
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              {lastUpdated && (
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(lastUpdated, { addSuffix: true })}
                </span>
              )}
              <Button variant="ghost" size="sm" onClick={refresh} disabled={isRefreshing}>
                <RefreshCw className={`w-4 h-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
            <span>{stats.total} tokens analyzed</span>
            <span>·</span>
            <span className="text-primary">{stats.spiking} spiking</span>
            <span>·</span>
            <span className="text-success">{stats.bull} bullish divergences</span>
            <span>·</span>
            <span className="text-warning">{stats.early} AltRank movers</span>
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          {emptyDay ? (
            <div className="text-center py-10 text-muted-foreground">
              <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No social signals collected yet</p>
              <p className="text-xs mt-1">Click Refresh to pull fresh data from LunarCrush.</p>
            </div>
          ) : (
            <>
              {/* Narrative grid — clickable to filter */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(narrativeStats)
                  .sort((a, b) => b[1].avgChange - a[1].avgChange)
                  .slice(0, 8)
                  .map(([n, d]) => {
                    const cfg = NARRATIVE_LABELS[n] || NARRATIVE_LABELS.other;
                    const isActive = activeNarrative === n;
                    const hot = d.spiking > 0;
                    return (
                      <button
                        key={n}
                        onClick={() => setActiveNarrative(isActive ? null : n)}
                        className={`p-2 rounded-lg border text-left transition-all ${
                          isActive ? 'border-primary bg-primary/10' :
                          hot ? 'border-primary/40 bg-primary/5 hover:border-primary/60' :
                          'border-border bg-secondary/30 hover:border-border/80'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm">{cfg.emoji}</span>
                          {hot && <Zap className="w-3 h-3 text-primary animate-pulse" />}
                        </div>
                        <div className="text-xs font-medium text-foreground">{cfg.label}</div>
                        <div className="flex items-center justify-between mt-1">
                          <div className="flex items-center gap-1">
                            {d.avgChange > 0 ? <TrendingUp className="w-3 h-3 text-success" /> : <TrendingDown className="w-3 h-3 text-destructive" />}
                            <span className={`text-[10px] font-mono ${d.avgChange > 0 ? 'text-success' : 'text-destructive'}`}>
                              {d.avgChange > 0 ? '+' : ''}{d.avgChange.toFixed(0)}%
                            </span>
                          </div>
                          {d.avgGalaxy > 0 && (
                            <span className="text-[9px] text-muted-foreground">G{d.avgGalaxy.toFixed(0)}</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
              </div>

              {activeNarrative && (
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="outline" className="gap-1">
                    Filter: {NARRATIVE_LABELS[activeNarrative]?.label || activeNarrative}
                    <button onClick={() => setActiveNarrative(null)} className="ml-1 hover:text-destructive">×</button>
                  </Badge>
                </div>
              )}

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as Tab)}>
                <TabsList className="grid w-full grid-cols-4 h-8">
                  <TabsTrigger value="opportunities" className="text-xs">🎯 Opportunities</TabsTrigger>
                  <TabsTrigger value="spiking" className="text-xs">🔥 Spiking</TabsTrigger>
                  <TabsTrigger value="altrank" className="text-xs">⭐ AltRank</TabsTrigger>
                  <TabsTrigger value="all" className="text-xs">📊 All</TabsTrigger>
                </TabsList>

                <TabsContent value={activeTab} className="mt-3">
                  {filtered.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm font-medium">
                        {activeTab === 'opportunities' && 'Quiet day — no significant bullish divergences detected.'}
                        {activeTab === 'spiking' && 'No tokens spiking above their 7-day baseline right now.'}
                        {activeTab === 'altrank' && 'No major AltRank movers in the last 24h.'}
                        {activeTab === 'all' && 'No tokens match this filter.'}
                      </p>
                      <p className="text-xs mt-1 opacity-70">Markets aren't always loud — that's a signal too.</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                      {filtered.slice(0, 20).map(s => <SignalRow key={s.id} s={s} />)}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}

          <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border">
            <AlertTriangle className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Spike thresholds use each token's own 7-day baseline + 75th-percentile gate.
              Divergence = social momentum minus 3× price momentum. Cross-reference with TOSS scores before entering.
            </p>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}

function SignalRow({ s }: { s: SocialSignal }) {
  const cfg = NARRATIVE_LABELS[s.narrative || 'other'] || NARRATIVE_LABELS.other;
  const isBullish = s.signal_type === 'bullish_divergence';
  const isEarly = s.signal_type === 'early_warning';
  const isMomentum = s.signal_type === 'momentum';
  const baselineForming = (s.baseline_days || 0) < 3;
  const altDelta = s.alt_rank_previous && s.alt_rank ? s.alt_rank_previous - s.alt_rank : 0;
  const div = s.price_vs_social_divergence || 0;

  return (
    <div className={`p-3 rounded-lg border transition-all ${
      isBullish ? 'bg-success/5 border-success/30' :
      isEarly ? 'bg-warning/5 border-warning/30' :
      isMomentum ? 'bg-primary/5 border-primary/30' :
      s.is_spiking ? 'bg-primary/5 border-primary/20' :
      'bg-secondary/30 border-border'
    }`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-lg">{cfg.emoji}</div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-foreground">{s.token_symbol}</span>
              <Badge className={`text-[10px] px-1.5 py-0 ${cfg.color}`}>{cfg.label}</Badge>
              {s.is_spiking && (
                <Badge className="text-[10px] px-1.5 py-0 bg-primary/20 text-primary border-primary/30 animate-pulse">
                  <Zap className="w-2.5 h-2.5 mr-0.5" />{s.spike_magnitude?.toUpperCase()}
                </Badge>
              )}
              {isEarly && (
                <Badge className="text-[10px] px-1.5 py-0 bg-warning/20 text-warning border-warning/30">
                  <Star className="w-2.5 h-2.5 mr-0.5" />EARLY
                </Badge>
              )}
              {baselineForming && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-0.5">
                      <HelpCircle className="w-2.5 h-2.5" />baseline forming
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[220px] text-xs">
                    Less than 3 days of data for this token — spike % is a placeholder until the baseline matures.
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            {s.ai_sentiment_summary && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.ai_sentiment_summary}</p>
            )}
          </div>
        </div>

        <div className="text-right flex-shrink-0">
          <div className="flex items-center gap-1 justify-end">
            {div > 0 ? <TrendingUp className="w-3 h-3 text-success" /> : <TrendingDown className="w-3 h-3 text-destructive" />}
            <span className={`text-sm font-mono font-bold ${div > 0 ? 'text-success' : 'text-destructive'}`}>
              {div > 0 ? '+' : ''}{div.toFixed(0)}
            </span>
          </div>
          <div className="text-[10px] text-muted-foreground">divergence</div>
        </div>
      </div>

      {/* Metrics row */}
      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border/50 text-xs flex-wrap">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1 cursor-help">
              <MessageCircle className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground">{s.twitter_mentions_24h?.toLocaleString() || '—'}</span>
              {!baselineForming && s.mention_change_pct != null && (
                <span className={s.mention_change_pct > 0 ? 'text-success' : 'text-destructive'}>
                  {s.mention_change_pct > 0 ? '+' : ''}{s.mention_change_pct.toFixed(0)}%
                </span>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent className="text-xs">
            Posts in last 24h vs {s.twitter_mentions_7d_avg?.toLocaleString() || '—'} 7-day avg
          </TooltipContent>
        </Tooltip>

        {s.galaxy_score != null && (
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">Galaxy</span>
            <span className="font-mono text-foreground">{s.galaxy_score.toFixed(0)}</span>
          </div>
        )}

        {s.alt_rank != null && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 cursor-help">
                <span className="text-muted-foreground">AltRank</span>
                <span className="font-mono text-foreground">#{s.alt_rank}</span>
                {altDelta > 0 && (
                  <span className="text-success flex items-center">
                    <ArrowUpRight className="w-3 h-3" />{altDelta}
                  </span>
                )}
                {altDelta < 0 && (
                  <span className="text-destructive flex items-center">
                    <ArrowDownRight className="w-3 h-3" />{Math.abs(altDelta)}
                  </span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent className="text-xs">
              Lower is better. {s.alt_rank_previous ? `Was #${s.alt_rank_previous} 4h ago.` : 'No prior rank yet.'}
            </TooltipContent>
          </Tooltip>
        )}

        {s.price_change_24h != null && (
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">24h</span>
            <span className={s.price_change_24h > 0 ? 'text-success' : 'text-destructive'}>
              {s.price_change_24h > 0 ? '+' : ''}{s.price_change_24h.toFixed(1)}%
            </span>
          </div>
        )}
      </div>

      {(s.catalyst_detected || (s.news_count_24h && s.news_count_24h > 3)) && (
        <div className="flex items-start gap-1 mt-2 p-2 rounded bg-warning/10 border border-warning/20">
          <Newspaper className="w-3 h-3 text-warning flex-shrink-0 mt-0.5" />
          <span className="text-[10px] text-warning">
            {s.catalyst_detected || `${s.news_count_24h} news articles in 24h`}
          </span>
        </div>
      )}

      {s.trending_keywords && s.trending_keywords.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {s.trending_keywords.slice(0, 4).map((k, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{k}</span>
          ))}
        </div>
      )}
    </div>
  );
}
